/**
 * ServiceOrdersTable - Tabela oficial de Ordens de Serviço
 *
 * Extraída de ServiceOrdersListPage para ser reutilizada sem duplicação na página
 * de listagem e nas abas O.S dos modais (Veículo/Cliente). Mesmas colunas,
 * ordenação, troca de fase agrupada por categoria e cronômetro.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-13 v1.2.0 - Selo de Tipo de Serviço em tom pastel discreto (helper compartilhado);
 *            fundo com fallback bg-primary quando o catálogo não tem cor.
 *   RELEASE: 2026-10-10 v1.1.0 - Espaçamento vertical das linhas reduzido ~15% (py-2.5 por célula),
 *            mantendo o conteúdo em h-10; cabeçalho e colunas permanecem iguais.
 *   RELEASE: 2026-10-09 v1.0.0 - Extração da tabela da listagem de OS (v1.9.6).
 */
import { CalendarClock } from 'lucide-react';
import { SortableHeader } from '@/components/layout/list-page';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useMoveServiceOrderPhase, type ServiceOrderSortField } from '@/hooks/useServiceOrders';
import { useServiceOrderPhasePermissions } from '@/hooks/useServiceOrderPhasePermissions';
import { ServiceOrderStatusBadge } from '@/components/service-orders/ServiceOrderPresentation';
import { ServiceOrderDeadline } from '@/components/service-orders/ServiceOrderTiming';
import { ServiceOrderTimerCell } from '@/components/service-orders/ServiceOrderTimerCell';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { PRIORITY_BAR_COLORS, PhaseDropdown } from '@/components/tasks/list/taskListShared';
import { formatStockDateTime } from '@/lib/stock-presentation';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { cn } from '@/lib/utils';
import { serviceTypeBadgeStyle } from '@/lib/utils/serviceTypeBadge';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  items: any[];
  isLoading: boolean;
  sortField: ServiceOrderSortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: ServiceOrderSortField) => void;
  onOpen: (id: string) => void;
}

export function ServiceOrdersTable({ items, isLoading, sortField, sortDirection, onSort, onOpen }: Props) {
  const { user } = useAuth();
  const { canEdit, isAdmin } = usePermissionsContext() as any;
  // BR-OS-021 v3: seletor de fase igual ao da Retenção (todas as categorias)
  const { canDoOnPhase: canDoOnOsPhase } = useServiceOrderPhasePermissions();
  const moveOsPhase = useMoveServiceOrderPhase();
  const canControlTimer = isAdmin || (!!canEdit?.('os_list') && !!canEdit?.('os_param_start'));
  const sh = (label: string, field: ServiceOrderSortField) => (
    <SortableHeader label={label} field={field} currentField={sortField} currentDirection={sortDirection} onSort={onSort} />
  );

  return (
    <Table className="min-w-[1820px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-8 px-2" aria-label="Indicador visual" />
          {sh('Número', 'number')}
          {sh('Cliente', 'customer')}
          {sh('Prestador', 'provider')}
          {sh('Tipo de serviço', 'service_type')}
          <TableHead className="text-xs font-medium">Veículo</TableHead>
          <TableHead className="text-xs font-medium">Categoria</TableHead>
          <TableHead className="text-xs font-medium">Fase</TableHead>
          {sh('Cronômetro', 'timer')}
          {sh('Situação', 'status')}
          <TableHead className="text-xs font-medium">Prioridade</TableHead>
          {sh('Prazo', 'deadline')}
          <TableHead className="text-xs font-medium"><span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Agend.</span></TableHead>
          {sh('Agendada para', 'scheduled_start')}
          {sh('Abertura', 'created_at')}
          {sh('Conclusão', 'completed_at')}
        </TableRow>
      </TableHeader>
      {/* v1.1.0: py-2.5 por célula (antes p-4) para aproximar as linhas da listagem.
          O estado vazio usa div interno com py-10 para não perder o respiro. */}
      <TableBody className="[&>tr>td]:py-2.5">
        {isLoading ? Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}><TableCell colSpan={16}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
        )) : items.length === 0 ? (
          <TableRow><TableCell colSpan={16} className="text-center text-sm text-muted-foreground"><div className="py-10">Nenhuma ordem de serviço encontrada.</div></TableCell></TableRow>
        ) : items.map((o) => (
          <TableRow key={o.id} className="cursor-pointer" onClick={() => onOpen(o.id)}>
            <TableCell className="px-2"><div className={cn('h-8 w-1 rounded-full', PRIORITY_BAR_COLORS[o.priority === 'medium' ? 'normal' : o.priority] ?? 'bg-muted-foreground/30')} title={PRIORITY_LABELS[o.priority]} /></TableCell>
            <TableCell><Badge variant="outline" className="h-5 max-w-[132px] whitespace-nowrap px-1.5 text-[10px] font-medium tabular-nums">{o.number}</Badge></TableCell>
            <TableCell><div className="flex h-10 max-w-[150px] flex-col justify-center overflow-hidden"><span className="truncate text-sm font-medium">{o.customer_name ?? '—'}</span>{o.customer_document && <span className="truncate text-xs text-muted-foreground">{formatDocumentForDisplay(o.customer_document)}</span>}</div></TableCell>
            <TableCell><div className="flex h-10 max-w-[150px] flex-col justify-center overflow-hidden"><span className="truncate text-sm font-medium">{o.provider_name || "Sem prestador"}</span>{o.provider_document && <span className="truncate text-xs text-muted-foreground">{formatDocumentForDisplay(o.provider_document)}</span>}</div></TableCell>
            {/* Selo do tipo de serviço: altura reduzida em 15% (20px -> 17px), fonte preservada
                e cor do catálogo em tom pastel discreto, igual ao app do prestador. */}
            <TableCell><span className="inline-flex h-[17px] max-w-[140px] items-center rounded-full bg-primary px-2 text-[10px] font-semibold text-primary-foreground whitespace-nowrap" style={serviceTypeBadgeStyle(o.service_type_color)}><span className="truncate">{o.service_type_name}</span></span></TableCell>
            <TableCell>{o.vehicle_plate ? <div className="flex h-10 max-w-[150px] flex-col justify-center overflow-hidden"><span className="truncate font-mono text-xs font-medium">{o.vehicle_plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}</span><span className="truncate text-xs text-muted-foreground">{o.vehicle_label}</span></div> : '—'}</TableCell>
            <TableCell><Badge variant="outline" className="h-5 max-w-[110px] px-1.5 text-[10px]"><span className="mr-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: o.category_color }} /><span className="truncate">{o.category_name}</span></Badge></TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <PhaseDropdown
                task={{ id: o.id, phase_id: o.phase_id ?? null, phase_name: o.phase_name, phase_color: o.phase_color, phase_sort_order: o.phase_sort_order ?? 0, total_category_phases: o.total_category_phases ?? 0 }}
                disabled={o.status === 'completed' || o.status === 'cancelled' || !(isAdmin || canEdit?.('os_kanban')) || !canDoOnOsPhase(o.phase_id, 'can_change_phase', o.responsible_user_id === user?.id)}
                canMoveToPhase={(phaseId) => canDoOnOsPhase(phaseId, 'can_change_phase', o.responsible_user_id === user?.id)}
                onMove={(phaseId, categoryId) => moveOsPhase.mutateAsync({ id: o.id, phaseId, categoryId, note: null }).then(() => undefined)}
              />
            </TableCell>
            <TableCell className="whitespace-nowrap"><ServiceOrderTimerCell serviceOrderId={o.id} status={o.status} seconds={o.active_seconds ?? 0} isPaused={o.is_paused} canControl={canControlTimer && canDoOnOsPhase(o.phase_id, 'can_change_status', o.responsible_user_id === user?.id)} /></TableCell>
            <TableCell className="whitespace-nowrap"><ServiceOrderStatusBadge status={o.status} overdue={o.is_overdue} /></TableCell>
            <TableCell><div className="flex items-center gap-1.5 whitespace-nowrap"><PriorityFlag priority={o.priority} size={12} /><span className="text-xs">{PRIORITY_LABELS[o.priority] ?? o.priority}</span></div></TableCell>
            <TableCell className="whitespace-nowrap"><ServiceOrderDeadline seconds={o.deadline_seconds ?? 0} compact /></TableCell>
            <TableCell className="text-center"><span className="inline-flex min-w-7 items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums">{o.schedule_count ?? 0}</span></TableCell>
            <TableCell className="whitespace-nowrap text-xs">{o.scheduled_start ? formatStockDateTime(o.scheduled_start) : '—'}</TableCell>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatStockDateTime(o.created_at)}</TableCell>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{o.completed_at ? formatStockDateTime(o.completed_at) : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
